// User / role management — 05-admin §8. GET /v1/admin/users(/{id}),
// PATCH /v1/admin/users/{id}/role (admin), POST /v1/admin/users/{id}/suspend (moderator).
import { api, errMsg } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { navigate } from "../core/router.js";
import type { AdminUser, Page } from "../models.js";

const ROLES: AdminUser["role"][] = ["viewer", "creator", "moderator", "admin"];

function statusBadge(s: AdminUser["status"]): HTMLElement {
  const cls = s === "active" ? "ok" : s === "suspended" ? "warn" : "danger";
  return h("span", { class: `badge ${cls}` }, s);
}

export async function usersView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const queryField = h("input", { class: "field", placeholder: "handle / email 검색" }) as HTMLInputElement;
  const listWrap = h("div", { class: "card" });

  async function load(): Promise<void> {
    listWrap.replaceChildren(h("div", { class: "state muted" }, "유저 불러오는 중…"));
    try {
      const q = queryField.value.trim();
      const path = `/v1/admin/users?limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`;
      const page = await api.get<Page<AdminUser>>(path);
      if (page.items.length === 0) {
        listWrap.replaceChildren(h("div", { class: "state muted" }, "유저가 없습니다."));
        return;
      }
      const table = h(
        "table",
        { class: "tbl" },
        h(
          "thead",
          {},
          h(
            "tr",
            {},
            h("th", {}, "handle"),
            h("th", {}, "이름"),
            h("th", {}, "role"),
            h("th", {}, "상태"),
            h("th", {}, "trust"),
            h("th", {}, ""),
          ),
        ),
        h(
          "tbody",
          {},
          ...page.items.map((u) =>
            h(
              "tr",
              {},
              h("td", {}, h("span", { class: "mono" }, "@" + u.handle)),
              h("td", {}, u.display_name),
              h("td", {}, h("span", { class: "badge" }, u.role)),
              h("td", {}, statusBadge(u.status)),
              h("td", { class: "muted small" }, u.trust_score != null ? u.trust_score.toFixed(2) : "—"),
              h("td", {}, h("button", { class: "btn small", onclick: () => navigate(`/users/${u.id}`) }, "상세")),
            ),
          ),
        ),
      );
      listWrap.replaceChildren(table);
    } catch (e) {
      listWrap.replaceChildren(h("div", { class: "state error" }, `유저 불러오기 실패: ${errMsg(e)}`));
    }
  }

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "유저 / 역할 관리"),
      h(
        "p",
        { class: "muted" },
        "정지/해제는 moderator+, role 변경은 admin (05-admin §8.1). 변경 시 token_ver bump로 전 세션 무효화 → 감사 로그.",
      ),
      h(
        "div",
        { class: "row" },
        queryField,
        h("button", { class: "btn", onclick: () => void load() }, "검색"),
      ),
      listWrap,
    ),
  );
  await load();
}

export async function userDetailView(params: Record<string, string>, root: HTMLElement): Promise<void> {
  const id = params["id"] ?? "";
  mount(root, h("div", { class: "stack" }, h("div", { class: "state muted" }, "유저 상세 불러오는 중…")));

  let user: AdminUser;
  try {
    user = await api.get<AdminUser>(`/v1/admin/users/${id}`);
  } catch (e) {
    mount(
      root,
      h(
        "div",
        { class: "stack" },
        h("a", { class: "back", href: "/users", "data-link": "true" }, "← 유저 목록"),
        h("div", { class: "state error" }, `유저 상세 실패: ${errMsg(e)}`),
      ),
    );
    return;
  }

  const status = h("p", { class: "small muted" }, "");
  const roleSelect = h(
    "select",
    { class: "field" },
    ...ROLES.map((r) => h("option", { value: r, selected: r === user.role }, r)),
  ) as HTMLSelectElement;

  async function changeRole(): Promise<void> {
    const newRole = roleSelect.value;
    if (newRole === user.role) {
      status.textContent = "변경된 role이 없습니다.";
      status.className = "small muted";
      return;
    }
    status.textContent = "role 변경 중…";
    status.className = "small muted";
    try {
      // PATCH /v1/admin/users/{id}/role (admin) — token_ver bump server-side.
      await api.patch(`/v1/admin/users/${id}/role`, { role: newRole });
      status.textContent = `role을 ${newRole}로 변경했습니다 (전 세션 무효화).`;
      status.className = "small ok";
      user = { ...user, role: newRole as AdminUser["role"] };
    } catch (e) {
      status.textContent = `role 변경 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  async function toggleSuspend(): Promise<void> {
    const suspend = user.status !== "suspended";
    const reason = window.prompt(suspend ? "정지 사유 (감사 로그)" : "해제 사유 (감사 로그)");
    if (reason === null) return;
    status.textContent = "처리 중…";
    status.className = "small muted";
    try {
      // POST /v1/admin/users/{id}/suspend (moderator) — token_ver bump server-side.
      await api.post(`/v1/admin/users/${id}/suspend`, { suspend, reason });
      user = { ...user, status: suspend ? "suspended" : "active" };
      render();
    } catch (e) {
      status.textContent = `처리 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  function render(): void {
    mount(
      root,
      h(
        "div",
        { class: "stack" },
        h("a", { class: "back", href: "/users", "data-link": "true" }, "← 유저 목록"),
        h("h1", {}, user.display_name, " ", h("span", { class: "mono muted" }, "@" + user.handle)),
        h(
          "section",
          { class: "card" },
          h("h2", {}, "기본 정보"),
          h(
            "dl",
            { class: "kv" },
            h("dt", {}, "id"),
            h("dd", { class: "mono" }, user.id),
            h("dt", {}, "email"),
            h("dd", {}, user.email ?? "—"),
            h("dt", {}, "role"),
            h("dd", {}, h("span", { class: "badge" }, user.role)),
            h("dt", {}, "상태"),
            h("dd", {}, statusBadge(user.status)),
            h("dt", {}, "trust_score"),
            h("dd", {}, user.trust_score != null ? user.trust_score.toFixed(2) : "—"),
            h("dt", {}, "policy_version"),
            h("dd", {}, user.policy_version ?? "—"),
          ),
        ),
        h(
          "section",
          { class: "card" },
          h("h2", {}, "조치"),
          h("label", { class: "lbl" }, "역할 변경 (admin)"),
          h("div", { class: "row" }, roleSelect, h("button", { class: "btn", onclick: () => void changeRole() }, "role 변경")),
          h(
            "div",
            { class: "row" },
            h(
              "button",
              { class: `btn ${user.status === "suspended" ? "" : "danger"}`, onclick: () => void toggleSuspend() },
              user.status === "suspended" ? "정지 해제" : "계정 정지",
            ),
          ),
          status,
        ),
      ),
    );
  }

  render();
}
